// QuotePage.jsx — /quote
// Ports the legacy Google-Forms-backed booking form into the current navy/gold
// site brand and posts to the existing `bookingFormSubmit` Cloud Function
// (writes to Firestore `bookings` collection, which is the CRM's source of
// truth; CRM's morning briefing + auto-sync pipelines all pick up from there).
//
// Pre-fill: a `?service={slug}` query param pre-ticks the matching service
// checkbox without announcing itself. No banner, no "we see you clicked X" —
// just a quietly pre-filled box the user can uncheck.
//
// Spam filter matches the endpoint's expectations: a honeypot `website_url`
// input (hidden, must stay empty) and a `formRenderedAt` timestamp (server
// flags submissions that happen in <2s as bot fills).

import { useEffect, useMemo, useRef, useState } from "react";
import { trackQuoteSubmit } from "../../utils/analytics";
import PanBar from "./components/PanBar";
import { CATEGORIES } from "./sections/Packages";

// Endpoint — rewritten to the Cloud Run service by firebase.json.
const FORM_ENDPOINT = "/api/bookingFormSubmit";

// Session token for Places Autocomplete (New) billing grouping. One session =
// all keystrokes of a single user's typing + the final place-details fetch,
// billed as a single "session" instead of per-keystroke. Regenerated on mount.
// https://developers.google.com/maps/documentation/places/web-service/place-session-tokens
const PLACES_API_BASE = "https://places.googleapis.com/v1";

// Top-level service buckets — kept intentionally short so the form is a
// quick triage, not a catalog menu. Specific offerings (from Packages.jsx)
// are captured silently in the notes on submit when the visitor arrived
// via a "Get a quote →" button with ?service=slug.
const SERVICE_BUCKETS = [
  ...CATEGORIES.map((cat) => ({ key: cat.key, label: cat.title })),
  { key: "something-else", label: "Something else" },
];

// Map any Packages slug → its parent category key, plus the full offering
// title for CRM context. Used to pre-tick the right bucket and to stash the
// specific offering in the notes field on submit.
const SLUG_LOOKUP = (() => {
  const map = {};
  for (const cat of CATEGORIES) {
    const all = [...cat.items, ...(cat.addOns || [])];
    for (const item of all) {
      map[item.slug] = { categoryKey: cat.key, title: item.title };
    }
  }
  return map;
})();

function readServiceFromUrl() {
  try {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("service") || null;
  } catch { return null; }
}

export default function QuotePage({ navigate }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const formRenderedAt = useRef(Date.now());

  // ── form state ──
  // If the visitor arrived via a "Get a quote →" button, map the incoming
  // slug to its parent category bucket and pre-tick that. The specific
  // offering title is held in a ref and appended to notes on submit — the
  // form itself doesn't mention it (quiet pre-fill per Dan's pref).
  const slugFromUrl = useMemo(readServiceFromUrl, []);
  const slugMeta = slugFromUrl ? SLUG_LOOKUP[slugFromUrl] : null;
  const [name, setName]               = useState("");
  const [company, setCompany]         = useState("");
  const [email, setEmail]             = useState("");
  const [phone, setPhone]             = useState("");
  const [eventDate, setEventDate]     = useState("");
  const [flexibleDate, setFlexibleDate] = useState(false);
  const [preferredTime, setPreferredTime] = useState("");
  const [location, setLocation]       = useState("");
  const [indoorOutdoor, setIndoorOutdoor] = useState("");
  const [attendance, setAttendance]   = useState("");
  const [services, setServices]       = useState(() => slugMeta ? [slugMeta.categoryKey] : []);
  const [addOns, setAddOns]           = useState([]);
  const [guestExperience, setGuestExperience] = useState("");
  const [performanceFormat, setPerformanceFormat] = useState("");
  const [budget, setBudget]           = useState("");
  const [notes, setNotes]             = useState("");
  const [honeypot, setHoneypot]       = useState(""); // bots fill this; humans don't see it

  // Scroll to top on mount so the success card + form land cleanly
  useEffect(() => { window.scrollTo(0, 0); }, []);

  function toggleService(slug) {
    setServices((cur) => cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]);
  }
  function toggleAddOn(slug) {
    setAddOns((cur) => cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Translate bucket keys → human labels for the CRM.
      const serviceLabels = services.map((key) => {
        const b = SERVICE_BUCKETS.find((x) => x.key === key);
        return b ? b.label : key;
      });

      // If the visitor arrived via a Packages card, preserve the specific
      // offering they clicked by prepending it to the notes. This is invisible
      // in the form UI but shows up in the CRM inquiry body.
      const notesWithContext = slugMeta
        ? `[Clicked "${slugMeta.title}"]${notes.trim() ? "\n\n" + notes.trim() : ""}`
        : (notes.trim() || null);

      const payload = {
        name: name.trim(),
        company: company.trim() || null,
        email: email.trim(),
        phone: phone.trim(),
        event_date: eventDate || null,
        flexible_date: flexibleDate,
        preferred_time: preferredTime || null,
        location: location.trim(),
        indoor_outdoor: indoorOutdoor,
        attendance: attendance,
        services: serviceLabels.join(", "),
        add_ons: addOns.length > 0 ? addOns.map(slug => {
          const liveEvents = CATEGORIES.find(c => c.key === "live-events");
          const match = (liveEvents?.addOns || []).find(a => a.slug === slug);
          return match ? match.title : slug;
        }).join(", ") : null,
        guest_experience: guestExperience,
        performance_format: performanceFormat,
        budget,
        client_notes: notesWithContext,
        // Spam filter fields
        website_url: honeypot,
        formRenderedAt: formRenderedAt.current,
      };

      const resp = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `Server returned ${resp.status}`);
      }
      setSubmitted(true);
      trackQuoteSubmit(serviceLabels.join(", "));
    } catch (err) {
      setError(err.message || "Something went wrong. Try again in a moment, or email hello@thejoyofpancakes.com.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: `radial-gradient(ellipse 140% 95% at 50% 0%,
        #1f59db 0%,
        #1a4b9e 30%,
        #0e2e46 70%,
        #081a2a 100%)`,
      padding: "3rem 1rem 5rem",
      fontFamily: "var(--jop-display)",
      color: "var(--jop-cream)",
      boxSizing: "border-box",
    }}>
      <div style={{ maxWidth: "680px", margin: "0 auto" }}>

        {/* Back to site */}
        <div style={{ marginBottom: "1.25rem", textAlign: "center" }}>
          <button
            onClick={() => (navigate ? navigate("/") : (window.location.href = "/"))}
            style={{
              background: "transparent",
              border: "1px solid rgba(255,218,173,0.25)",
              color: "rgba(255,218,173,0.75)",
              borderRadius: "999px",
              padding: "0.4rem 0.9rem",
              fontFamily: "var(--jop-display)",
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ← Back to thejoyofpancakes.com
          </button>
        </div>

        {/* White card — echoes the Packages sections being dark with a bright
            inner slab for readability. */}
        <div style={{
          background: "#ffffff",
          color: "#1a1a1a",
          borderRadius: "18px",
          padding: "2.75rem 2rem 3rem",
          boxShadow: "0 8px 48px rgba(0,0,0,0.3)",
        }}>

          {submitted ? (
            <SuccessCard />
          ) : (
            <>
              <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                <PanBar width={60} height={3} margin="0 auto 0.9rem" />
                <h1 style={{
                  fontFamily: "var(--jop-script)",
                  fontSize: "clamp(2.2rem, 6vw, 2.8rem)",
                  color: "#1d52a0",
                  fontWeight: 700,
                  margin: "0 0 0.5rem",
                  lineHeight: 1.1,
                }}>
                  You're gonna flip...
                </h1>
                <p style={{ color: "#555", fontSize: "0.98rem", margin: 0, lineHeight: 1.5 }}>
                  Complete this form and get ready to experience The Joy of Pancakes.
                </p>
              </div>

              <form onSubmit={handleSubmit}>

                {/* Honeypot — hidden from users, bots fill it */}
                <input
                  type="text"
                  name="website_url"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
                  aria-hidden="true"
                />

                {/* SECTION: Who are you? */}
                <Section title="Who are you?">
                  <Row>
                    <Field label="Your name" required>
                      <Input value={name} onChange={setName} placeholder="Jane Smith" required />
                    </Field>
                    <Field label="Company name">
                      <Input value={company} onChange={setCompany} placeholder="Optional" />
                    </Field>
                  </Row>
                  <Row>
                    <Field label="Email" required>
                      <Input type="email" value={email} onChange={setEmail} placeholder="jane@example.com" required />
                    </Field>
                    <Field label="Phone number" required>
                      <Input type="tel" value={phone} onChange={setPhone} placeholder="(314) 555-0100" required />
                    </Field>
                  </Row>
                </Section>

                {/* SECTION: Event */}
                <Section title="Tell us about your event">
                  <Row>
                    <Field label="Event date" required>
                      <Input type="date" value={eventDate} onChange={setEventDate} />
                      <label style={flexibleLabel}>
                        <input type="checkbox" checked={flexibleDate} onChange={(e) => setFlexibleDate(e.target.checked)} style={{ accentColor: "#1d52a0", width: 16, height: 16 }} />
                        My date is flexible
                      </label>
                    </Field>
                    <Field label="Preferred start time">
                      <Input type="time" value={preferredTime} onChange={setPreferredTime} />
                    </Field>
                  </Row>
                  <Row single>
                    <Field label="Event location" required>
                      <CityAutocomplete value={location} onChange={setLocation} placeholder="Start typing a city…" required />
                    </Field>
                  </Row>
                  <Row>
                    <Field label="Indoors or outdoors?" required>
                      <ChoiceList
                        options={["Indoors", "Outdoors", "Both / Not sure"]}
                        value={indoorOutdoor}
                        onChange={setIndoorOutdoor}
                        name="indoor_outdoor"
                        type="radio"
                      />
                    </Field>
                    <Field label="Expected attendance" required>
                      <Select value={attendance} onChange={setAttendance} required>
                        <option value="">Select one…</option>
                        <option value="Under 50">Under 50</option>
                        <option value="50-150">50–150</option>
                        <option value="150-500">150–500</option>
                        <option value="500+">500+</option>
                      </Select>
                    </Field>
                  </Row>
                </Section>

                {/* SECTION: What you want */}
                <Section title="What are you looking for?">
                  <Legend>Which services are you interested in? <span style={{ color: "#777", fontWeight: 400 }}>(select all that apply)</span></Legend>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {SERVICE_BUCKETS.map((b) => (
                      <Choice
                        key={b.key}
                        checked={services.includes(b.key)}
                        onChange={() => toggleService(b.key)}
                        label={b.label}
                        type="checkbox"
                      />
                    ))}
                  </div>

                  {services.includes("live-events") && (
                    <div style={{ marginTop: "1.25rem" }}>
                      <Legend>Interested in any add-ons? <span style={{ color: "#777", fontWeight: 400 }}>(optional)</span></Legend>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        {(CATEGORIES.find(c => c.key === "live-events")?.addOns || []).map(a => (
                          <Choice
                            key={a.slug}
                            checked={addOns.includes(a.slug)}
                            onChange={() => toggleAddOn(a.slug)}
                            label={a.title}
                            type="checkbox"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: "1.25rem" }}>
                    <Legend>What's the primary experience for your guests? <Req /></Legend>
                    <ChoiceList
                      options={[
                        "Watch Dan create art live",
                        "Guests receive a personalized pancake",
                        "Feed a large crowd efficiently",
                        "A mix — not sure yet",
                      ]}
                      value={guestExperience}
                      onChange={setGuestExperience}
                      name="guest_experience"
                      type="radio"
                    />
                  </div>

                  <div style={{ marginTop: "1.25rem" }}>
                    <Legend>How will the performance be set up? <Req /></Legend>
                    <ChoiceList
                      options={[
                        "Roaming & interactive (guests come to me)",
                        "Stage or spotlight (audience watches from a distance)",
                        "Not sure yet",
                      ]}
                      value={performanceFormat}
                      onChange={setPerformanceFormat}
                      name="performance_format"
                      type="radio"
                    />
                  </div>
                </Section>

                {/* SECTION: Budget */}
                <Section title="Budget">
                  <Field label="Estimated budget for this service" required>
                    <Select value={budget} onChange={setBudget} required>
                      <option value="">Select one…</option>
                      <option value="Under $1,000">Under $1,000</option>
                      <option value="$1,000–$2,500">$1,000–$2,500</option>
                      <option value="$2,500–$5,000">$2,500–$5,000</option>
                      <option value="$5,000–$10,000">$5,000–$10,000</option>
                      <option value="$10,000+">$10,000+</option>
                    </Select>
                  </Field>
                </Section>

                {/* SECTION: Anything else */}
                <Section title="Anything else?">
                  <Field label="Tell us anything we should know">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Special requests, unique venue details, VIP guests, tight timelines — tell us here."
                      rows={5}
                      style={{ ...inputStyle, resize: "vertical", minHeight: "110px", fontFamily: "inherit" }}
                    />
                  </Field>
                </Section>

                {error && (
                  <div style={{
                    background: "#fdecea",
                    color: "#c0392b",
                    border: "1px solid #f4b7b0",
                    padding: "0.75rem 1rem",
                    borderRadius: "8px",
                    fontSize: "0.88rem",
                    margin: "1rem 0",
                  }}>
                    {error}
                  </div>
                )}

                <div style={{ textAlign: "center", marginTop: "2rem" }}>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      background: submitting ? "#d0d0d0" : "#fcb31f",
                      color: "#1a0a12",
                      fontFamily: "var(--jop-script)",
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      padding: "0.8rem 2.8rem",
                      border: "none",
                      borderRadius: "50px",
                      cursor: submitting ? "default" : "pointer",
                      letterSpacing: "0.02em",
                      boxShadow: "0 4px 18px rgba(252,179,31,0.45)",
                      transition: "transform 0.15s ease, box-shadow 0.2s ease",
                    }}
                  >
                    {submitting ? "Sending…" : "Get a Quote!"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Success state ──
function SuccessCard() {
  return (
    <div style={{ textAlign: "center", padding: "2.5rem 1rem 1.5rem" }}>
      <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>🥞</div>
      <h2 style={{ fontFamily: "var(--jop-script)", color: "#1d52a0", fontSize: "2.2rem", margin: "0 0 0.75rem" }}>
        You're gonna flip!
      </h2>
      <p style={{ color: "#555", fontSize: "1rem", lineHeight: 1.6, margin: "0 auto", maxWidth: "440px" }}>
        We've received your inquiry and will be in touch soon.<br />
        Can't wait to make something amazing together.
      </p>
    </div>
  );
}

// ── Form primitives ──
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: "2rem" }}>
      <div style={{
        fontFamily: "var(--jop-script)",
        fontSize: "1.45rem",
        fontWeight: 600,
        color: "#1d52a0",
        borderBottom: "2px solid #e8e8e8",
        paddingBottom: "0.35rem",
        marginBottom: "1.1rem",
      }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ children, single }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: single ? "1fr" : "1fr 1fr",
      gap: "1rem",
      marginBottom: "1rem",
    }}>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
      <label style={{ fontSize: "0.88rem", fontWeight: 500, color: "#444" }}>
        {label}{required && <Req />}
      </label>
      {children}
    </div>
  );
}

function Req() {
  return <span style={{ color: "#1d52a0", marginLeft: 2 }}>*</span>;
}

const inputStyle = {
  border: "1.5px solid #d6e3f0",
  borderRadius: "6px",
  padding: "0.55rem 0.75rem",
  fontFamily: "inherit",
  fontSize: "0.95rem",
  color: "#1a1a1a",
  background: "#f9f9fa",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
};

function Input({ type = "text", value, onChange, placeholder, required }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...inputStyle,
        background: focused ? "#ffffff" : inputStyle.background,
        borderColor: focused ? "#1d52a0" : inputStyle.border.split(" ")[2],
        boxShadow: focused ? "0 0 0 3px rgba(29,82,160,0.15)" : "none",
      }}
    />
  );
}

// City-level autocomplete using the Places API (New) REST endpoints directly.
// No Google SDK load — we control the dropdown UI so it matches the form
// chrome. If the API call fails (offline, bad key, rate-limited), the user
// can still type freely; the raw text submits as-is.
function CityAutocomplete({ value, onChange, placeholder, required }) {
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [hoverIdx, setHoverIdx] = useState(-1);
  // One session token per mount so Google bills a full typing session as one
  // request instead of charging per keystroke.
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    sessionTokenRef.current =
      (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
  }, []);

  const fetchSuggestions = (input) => {
    const key = process.env.REACT_APP_GOOGLE_PLACES_KEY;
    if (!key || !input || input.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const resp = await fetch(`${PLACES_API_BASE}/places:autocomplete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": key,
          },
          body: JSON.stringify({
            input,
            // No type filter — cities, street addresses, AND venues/POIs should
            // all surface. An event can happen in a city (broad) or at a
            // specific venue / street address (granular); both are useful.
            includedRegionCodes: ["us"],
            sessionToken: sessionTokenRef.current,
          }),
        });
        if (!resp.ok) { setSuggestions([]); return; }
        const data = await resp.json();
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions.slice(0, 6) : []);
        setHoverIdx(-1);
      } catch (err) {
        console.warn("Places autocomplete error:", err);
        setSuggestions([]);
      }
    }, 220);
  };

  const selectSuggestion = async (suggestion) => {
    const pred = suggestion?.placePrediction;
    if (!pred) return;
    const predictionText = pred.text?.text || "";
    // Optimistic fill — if fetchFields succeeds we'll refine it to "City, ST"
    onChange(predictionText);
    setSuggestions([]);
    setHoverIdx(-1);

    const key = process.env.REACT_APP_GOOGLE_PLACES_KEY;
    if (!pred.placeId || !key) return;
    try {
      const resp = await fetch(`${PLACES_API_BASE}/places/${pred.placeId}?sessionToken=${encodeURIComponent(sessionTokenRef.current)}`, {
        headers: {
          "X-Goog-Api-Key": key,
          "X-Goog-FieldMask": "displayName,formattedAddress,addressComponents",
        },
      });
      if (!resp.ok) return;
      const place = await resp.json();
      const displayName = place.displayName?.text;
      const formatted = (place.formattedAddress || "").replace(/,\s*USA$/i, "").trim();

      // Preserve whatever specificity the user picked. Google's own
      // formattedAddress already collapses to "City, State" for city-level
      // suggestions and expands to the full street for venues — we shouldn't
      // re-collapse it ourselves. Prepend displayName only when it adds info
      // the formatted address doesn't already lead with.
      //
      // Previously this branched on `street_number` presence and silently
      // truncated venue picks (e.g. Tower Grove Farmers Market, a park with
      // no street number) down to "St. Louis, MO" — losing the address the
      // customer actually meant to share.
      let composed;
      if (displayName && formatted && !formatted.toLowerCase().startsWith(displayName.toLowerCase())) {
        composed = `${displayName}, ${formatted}`;
      } else {
        composed = formatted || displayName || "";
      }
      if (composed) onChange(composed);
      // Rotate token so the next selection starts a new billing session.
      sessionTokenRef.current =
        (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
        Math.random().toString(36).slice(2) + Date.now().toString(36);
    } catch (err) {
      console.warn("Place details fetch failed:", err);
    }
  };

  const handleKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHoverIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHoverIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && hoverIdx >= 0) { e.preventDefault(); selectSuggestion(suggestions[hoverIdx]); }
    else if (e.key === "Escape") { setSuggestions([]); setHoverIdx(-1); }
  };

  const showDropdown = focused && suggestions.length > 0;

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); fetchSuggestions(e.target.value); }}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        onKeyDown={handleKeyDown}
        style={{
          ...inputStyle,
          background: focused ? "#ffffff" : inputStyle.background,
          borderColor: focused ? "#1d52a0" : inputStyle.border.split(" ")[2],
          boxShadow: focused ? "0 0 0 3px rgba(29,82,160,0.15)" : "none",
          borderBottomLeftRadius: showDropdown ? 0 : 4,
          borderBottomRightRadius: showDropdown ? 0 : 4,
        }}
      />
      {showDropdown && (
        <ul style={{
          listStyle: "none",
          margin: 0, padding: 0,
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
          background: "#ffffff",
          border: "1.5px solid #1d52a0",
          borderTop: "1px solid rgba(29,82,160,0.15)",
          borderBottomLeftRadius: 4, borderBottomRightRadius: 4,
          boxShadow: "0 10px 28px rgba(13,24,51,0.18)",
          maxHeight: 280, overflowY: "auto",
        }}>
          {suggestions.map((s, i) => {
            const pred = s.placePrediction;
            const main = pred?.structuredFormat?.mainText?.text || pred?.text?.text || "";
            const sec  = pred?.structuredFormat?.secondaryText?.text || "";
            return (
              <li
                key={pred?.placeId || i}
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                onMouseEnter={() => setHoverIdx(i)}
                style={{
                  padding: "0.6rem 0.85rem",
                  cursor: "pointer",
                  background: hoverIdx === i ? "rgba(29,82,160,0.08)" : "transparent",
                  borderBottom: i < suggestions.length - 1 ? "1px solid rgba(29,82,160,0.08)" : "none",
                  fontSize: "0.9rem",
                }}
              >
                <div style={{ color: "#1d52a0", fontWeight: 600 }}>{main}</div>
                {sec && <div style={{ color: "#777", fontSize: "0.78rem", marginTop: 2 }}>{sec}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Select({ value, onChange, required, children }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      style={{
        ...inputStyle,
        appearance: "none",
        cursor: "pointer",
        paddingRight: "2.2rem",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%231d52a0' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 0.85rem center",
      }}
    >
      {children}
    </select>
  );
}

function Legend({ children }) {
  return (
    <div style={{
      fontSize: "0.88rem",
      fontWeight: 500,
      color: "#444",
      marginBottom: "0.6rem",
    }}>{children}</div>
  );
}

function Choice({ checked, onChange, label, type, name }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        cursor: "pointer",
        padding: "0.55rem 0.8rem",
        border: `1.5px solid ${checked ? "#1d52a0" : "#d6e3f0"}`,
        borderRadius: "6px",
        background: checked ? "rgba(29,82,160,0.06)" : "#f9f9fa",
        transition: "border-color 0.15s ease, background 0.15s ease",
        fontSize: "0.92rem",
      }}
    >
      <input
        type={type}
        checked={checked}
        onChange={onChange}
        name={name}
        style={{ accentColor: "#1d52a0", width: 16, height: 16, flexShrink: 0, cursor: "pointer" }}
      />
      {label}
    </label>
  );
}

function ChoiceList({ options, value, onChange, name, type }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {options.map((opt) => (
        <Choice
          key={opt}
          checked={value === opt}
          onChange={() => onChange(opt)}
          label={opt}
          type={type}
          name={name}
        />
      ))}
    </div>
  );
}

const flexibleLabel = {
  display: "flex",
  alignItems: "center",
  gap: "0.45rem",
  fontSize: "0.82rem",
  color: "#666",
  marginTop: "0.3rem",
  cursor: "pointer",
};
